import React, { useState, useEffect } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { NumericFormat } from 'react-number-format';
import config from '../config/config';
import { useToast } from './ToastContext';
import BarraCadastro from './BarraCadastro';
import { consultarCnpj, completarComDadosCnpj } from '../others/consultaCnpj';
import { situacaoCadastro, buscarClientePorDocumento, complementarCliente, criarCliente } from '../others/cadastroSync';
import {
  pessoaVazia, clienteParaPessoa, removerMascaraCPFCNPJ, mascaraCpfCnpj, formatarCep, buscarCep
} from '../others/documentosBase';

// ─── Dados estáticos ──────────────────────────────────────────────────────────

const MESES = [
  "janeiro", "fevereiro", "março", "abril", "maio", "junho",
  "julho", "agosto", "setembro", "outubro", "novembro", "dezembro"
];

const SERVICOS_ESQUERDA = [
  "2ª via do CRV",
  "Impressão do CRLVe",
  "Emissão de Certidão",
  "Cópia de documentos",
  "Licença Especial de Trânsito",
  "Alteração de Informações do Proprietário",
  "Alteração de Informações do Veículo",
  "Inclusão de Restrição Financeira",
  "Alteração de Restrição Financeira",
  "Liberação de Restrição Financeira",
  "Inclusão de Averbação de Execução",
  "Liberação de Averbação de Execução",
  "Restrição por Transferência",
  "Inclusão Placa de Experiência ou de Fabricante",
  "Renovação de Placa de Experiência ou de Fabricante",
  "Baixa de Placa de Experiência ou de Fabricante",
  "Baixa para outra UF",
  "Baixa simples de Veículo",
  "Baixa para militarização",
  "Baixa para outro país",
  "Cancelamento de Processo",
  "Comunicação de Venda",
];

const SERVICOS_DIREITA = [
  "Correção das Observações do CRLV-e",
  "Correção de Chassi",
  "Correção de Informações do Proprietário",
  "Correção de Informações do Veículo",
  "Correção de Município",
  "Correção de Restrições",
  "Mudança p/ Placa Única / Troca de Placa Mercosul",
  "Primeiro Emplacamento",
  "Reserva de Placa",
  "Autorização para Fabricação de Placas",
  "Colocação de lacre em placa",
  "Autorização Para Alteração de Características",
  "Autorização para Regravação de Nº de Chassi",
  "Autorização para Regravação de Nº de Motor",
  "Autorização para Transporte Escolar",
  "Solicitação de Vistoria",
  "Transferência de propriedade de Veículo de Outra UF",
  "Transferência de Propriedade de Veículo do RS",
  "Troca de Município de Veículo de Outra UF",
  "Troca de Município de Veículo do RS",
];

const SERVICOS_COM_SUBOPCAO = [
  "Autorização para Fabricação de Placas",
  "Colocação de lacre em placa",
];

// ─── Print Global Styles ──────────────────────────────────────────────────────

const PrintStyles = createGlobalStyle`
  @media print {
    @page {
      size: A4 portrait;
      margin: 1.2cm 0.8cm;
    }
    body {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    .no-print {
      display: none !important;
    }
    input::placeholder {
      color: transparent !important;
    }
    * {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
  }
`;

// ─── Formatadores ─────────────────────────────────────────────────────────────

const fmt = {
  cpf:  (v) => v ? v.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4') : '',
  cnpj: (v) => v ? v.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5') : '',
  tel:  (v) => {
    if (!v) return '';
    const d = v.replace(/\D/g, '');
    if (d.length === 11) return `(${d.slice(0,2)}) ${d.slice(2,7)}-${d.slice(7)}`;
    if (d.length === 10) return `(${d.slice(0,2)}) ${d.slice(2,6)}-${d.slice(6)}`;
    return v;
  },
};

// ═════════════════════════════════════════════════════════════════════════════
// FORMULÁRIO 1 — REQUERIMENTO DE SERVIÇO COM DECLARAÇÃO DE AUTENTICIDADE
// ═════════════════════════════════════════════════════════════════════════════

const FormServicoDeclaracao = ({ cliente, placaInicial, chassiInicial, cidadeEmpresa, onVeiculoChange }) => {
  const [servicosSelecionados, setServicosSelecionados] = useState([]);
  const [subOpcoes, setSubOpcoes]     = useState({});
  const [outroServico, setOutroServico] = useState('');
  const [placa, setPlaca]             = useState(placaInicial);
  const [chassi, setChassi]           = useState(chassiInicial);
  const [restrFin, setRestrFin]       = useState('');
  const [tipoRestricao, setTipoRestricao] = useState('');
  const [tipoCredor, setTipoCredor]   = useState('');
  const [agenteFin, setAgenteFin]     = useState('');
  const [autoEmail, setAutoEmail]     = useState('');
  const [identidade, setIdentidade]   = useState(cliente?.identidade || '');
  const [semCelular, setSemCelular]   = useState(false);
  const [semEmail, setSemEmail]       = useState(false);

  useEffect(() => { setPlaca(placaInicial); setChassi(chassiInicial); }, [placaInicial, chassiInicial]);
  useEffect(() => { setIdentidade(cliente?.identidade || ''); }, [cliente]);
  useEffect(() => { if (onVeiculoChange) onVeiculoChange({ placa, chassi }); }, [placa, chassi, onVeiculoChange]);

  const hoje = new Date();
  const dataAtual = {
    dia: String(hoje.getDate()).padStart(2, '0'),
    mes: MESES[hoje.getMonth()],
    ano: hoje.getFullYear(),
  };

  const nomeCliente = (cliente?.nome || cliente?.razaoSocial || '').toUpperCase();
  const docCliente  = cliente?.cpf ? fmt.cpf(cliente.cpf) : cliente?.cnpj ? fmt.cnpj(cliente.cnpj) : '';

  const enderecoCompleto = () => {
    if (!cliente) return '';
    const p = [];
    if (cliente.logradouro) p.push(cliente.logradouro.toUpperCase());
    if (cliente.numero)     p.push(cliente.numero);
    if (cliente.complemento) p.push(cliente.complemento.toUpperCase());
    if (cliente.bairro)     p.push(`– ${cliente.bairro.toUpperCase()}`);
    if (cliente.cidade)     p.push(`– ${cliente.cidade.toUpperCase()}`);
    if (cliente.uf)         p.push(`/${cliente.uf.toUpperCase()}`);
    if (cliente.cep)        p.push(`– CEP ${cliente.cep}`);
    return p.join(' ');
  };

  const toggleServico = (id) =>
    setServicosSelecionados(prev =>
      prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]
    );

  const toggleSubOpcao = (servico, opcao) =>
    setSubOpcoes(prev => ({ ...prev, [servico]: prev[servico] === opcao ? '' : opcao }));

  const renderServico = (nome) => {
    const temSub = SERVICOS_COM_SUBOPCAO.includes(nome);
    return (
      <ServicoLinha key={nome}>
        <CheckWrap>
          <input type="checkbox" checked={servicosSelecionados.includes(nome)} onChange={() => toggleServico(nome)} />
          <span>{nome}</span>
        </CheckWrap>
        {temSub && (
          <SubOpcoes>
            {['furto/roubo', 'perda/extravio', 'outros'].map(op => (
              <SubOpcaoLabel key={op}>
                <input type="radio" name={`sub_${nome}`} checked={subOpcoes[nome] === op} onChange={() => toggleSubOpcao(nome, op)} />
                {op}
              </SubOpcaoLabel>
            ))}
          </SubOpcoes>
        )}
      </ServicoLinha>
    );
  };

  return (
    <FormContainer className="print-area">
      <Titulo>
        ANEXO 1 - REQUERIMENTO DE SERVIÇO COM DECLARAÇÃO DE AUTENTICIDADE DE DOCUMENTOS
      </Titulo>

      {/* ─── CORREÇÃO: linha "Eu, NOME, Identidade nº" toda em linha inline ─── */}
      <BlocoIdentificacao>
        <LinhaInline>
          <span>Eu,&nbsp;</span>
          <NomeDestaque>{nomeCliente}</NomeDestaque>
          <SpanLinha />{/* linha pontilhada que ocupa o espaço restante antes de "Identidade" */}
          <span style={{ whiteSpace: 'nowrap' }}>,&nbsp;Identidade nº&nbsp;</span>
          <InputLinha
            value={identidade}
            onChange={e => setIdentidade(e.target.value.toUpperCase())}
            style={{ width: '110px' }}
            placeholder="nº identidade"
          />
        </LinhaInline>

        <LinhaTexto>
          <span>CPF nº </span><ValorInline>{docCliente}</ValorInline>
          <span> , Telefone nº </span><ValorInline>{fmt.tel(cliente?.telefone)}</ValorInline>
          <span> requeiro ao DETRAN/RS a realização do seguinte serviço, relativo ao(s) veículo(s) placa(s) </span>
          <InputLinha value={placa} onChange={e => setPlaca(e.target.value.toUpperCase())} placeholder="ABC-1234" maxLength={8} style={{ width: '75px' }} />
          <span> , chassi(s) </span>
          <InputLinha value={chassi} onChange={e => setChassi(e.target.value.toUpperCase())} placeholder="12345678901234567890" maxLength={20} style={{ width: '175px' }} />
          <span> :</span>
        </LinhaTexto>
      </BlocoIdentificacao>

      <ServicosContainer>
        <ServicosColuna>
          {SERVICOS_ESQUERDA.map(renderServico)}
          <ServicoLinha>
            <CheckWrap>
              <input type="checkbox" checked={servicosSelecionados.includes('outro')} onChange={() => toggleServico('outro')} />
              <span>Outro:</span>
            </CheckWrap>
            <InputOutro type="text" value={outroServico} onChange={e => setOutroServico(e.target.value)} placeholder="Descreva o serviço" />
          </ServicoLinha>
        </ServicosColuna>
        <ServicosColuna>
          {SERVICOS_DIREITA.map(renderServico)}
        </ServicosColuna>
      </ServicosContainer>

      <LinhaRestricao>
        <Rotulo>Haverá inclusão ou alteração de restrição financeira?</Rotulo>
        <RadioLabel><input type="radio" name="restrFin" onChange={() => setRestrFin('Sim')} /> Sim</RadioLabel>
        <RadioLabel><input type="radio" name="restrFin" onChange={() => setRestrFin('Não')} /> Não</RadioLabel>
      </LinhaRestricao>

      <LinhaRestricao>
        {['Arrendamento', 'Reserva de Domínio', 'Alienação Fiduciária', 'Penhor', 'Comodato', 'Locação'].map(tipo => (
          <RadioLabel key={tipo}>
            <input type="radio" name="tipoRestricao" onChange={() => setTipoRestricao(tipo)} /> {tipo}
          </RadioLabel>
        ))}
      </LinhaRestricao>

      <LinhaRestricao>
        <Rotulo>O credor é:</Rotulo>
        <RadioLabel><input type="radio" name="tipoCredor" onChange={() => setTipoCredor('Pessoa Física')} /> Pessoa Física</RadioLabel>
        <RadioLabel><input type="radio" name="tipoCredor" onChange={() => setTipoCredor('Pessoa Jurídica')} /> Pessoa Jurídica</RadioLabel>
      </LinhaRestricao>

      <LinhaRestricao>
        <Rotulo>Agente Financeiro:</Rotulo>
        <InputLinhaCrescente value={agenteFin} onChange={e => setAgenteFin(e.target.value)} />
      </LinhaRestricao>

      <SecaoBox>
        <SecaoTitulo>
          DADOS DO ADQUIRENTE (PROCESSOS 101, 102 E 103) OU DO PROPRIETÁRIO (DEMAIS PROCESSOS)
        </SecaoTitulo>
        <SecaoSubtitulo>
          (Preenchimento obrigatório dos campos abaixo, sendo vedada a inclusão de quaisquer dados de terceiros)
        </SecaoSubtitulo>

        <LinhaCampo>
          <Rotulo>Endereço:</Rotulo>
          <CampoValor style={{ flex: 1 }}>{enderecoCompleto()}</CampoValor>
        </LinhaCampo>

        <LinhaCampo>
          <Rotulo>Telefone celular:</Rotulo>
          <CampoValor style={{ width: '120px', flex: 'none' }}>{fmt.tel(cliente?.telefone)}</CampoValor>
          <CheckboxLabel>
            <input type="checkbox" checked={semCelular} onChange={e => setSemCelular(e.target.checked)} />
            Não possui celular
          </CheckboxLabel>
          <Rotulo style={{ marginLeft: '10px' }}>Email:</Rotulo>
          <EmailValor>{semEmail ? '' : (cliente?.email || '').toLowerCase()}</EmailValor>
          <CheckboxLabel>
            <input type="checkbox" checked={semEmail} onChange={e => setSemEmail(e.target.checked)} />
            Não possui Email
          </CheckboxLabel>
        </LinhaCampo>

        <LinhaCampo>
          <Rotulo>Autorizo o DETRAN/RS a enviar por email ou telefone celular informações de interesse junto a este órgão.</Rotulo>
          <RadioLabel><input type="radio" name="autoEmail" onChange={() => setAutoEmail('SIM')} /> SIM</RadioLabel>
          <RadioLabel><input type="radio" name="autoEmail" onChange={() => setAutoEmail('NÃO')} /> NÃO</RadioLabel>
        </LinhaCampo>
      </SecaoBox>

      <DeclaracaoBox>
        <DeclaracaoTexto>
          <strong>
            DECLARO, sob as penas da lei e sem prejuízo de demais sanções administrativas/cíveis/criminais, que
            os documentos entregues ao DETRAN/RS para o serviço requerido são todos autênticos e, quando cópias,
            condizem com o original e, caso haja produção de placa(s) de identificação veicular, que sou responsável
            por providenciar a estampagem e instalação da(s) placa(s) no veículo para o qual a estampagem foi
            autorizada, bem como pela entrega das placas retiradas/substituídas na EPIV.
          </strong>
        </DeclaracaoTexto>
      </DeclaracaoBox>

      <AssinaturaContainer>
        <MunicipioLinha>
          Município: <strong>{(cidadeEmpresa || '').toUpperCase()}</strong>, {dataAtual.dia} de {dataAtual.mes} de {dataAtual.ano}.
        </MunicipioLinha>
        <EspacoAssinatura />
        <AssinaturaLinha />
        <AssinaturaRotulo>Assinatura do Responsável</AssinaturaRotulo>
      </AssinaturaContainer>
    </FormContainer>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// FORMULÁRIO 2 — REQUERIMENTO DE PRIMEIRO EMPLACAMENTO
// ═════════════════════════════════════════════════════════════════════════════

const FormPrimeiroEmplacamento = ({ cliente, chassiInicial, cidadeEmpresa, onVeiculoChange }) => {
  const [chassi, setChassi]         = useState(chassiInicial);
  const [identidade, setIdentidade] = useState(cliente?.identidade || '');
  const [restrFin, setRestrFin]     = useState('');
  const [tipoRestricao, setTipoRestricao] = useState('');
  const [tipoCredor, setTipoCredor] = useState('');
  const [agenteFin, setAgenteFin]   = useState('');
  const [autoEmail, setAutoEmail]   = useState('');
  const [semCelular, setSemCelular] = useState(false);
  const [semEmail, setSemEmail]     = useState(false);

  useEffect(() => { setChassi(chassiInicial); }, [chassiInicial]);
  useEffect(() => { setIdentidade(cliente?.identidade || ''); }, [cliente]);
  useEffect(() => { if (onVeiculoChange) onVeiculoChange({ chassi }); }, [chassi, onVeiculoChange]);

  const hoje = new Date();
  const dataAtual = {
    dia: String(hoje.getDate()).padStart(2, '0'),
    mes: MESES[hoje.getMonth()],
    ano: hoje.getFullYear(),
  };

  const nomeCliente = (cliente?.nome || cliente?.razaoSocial || '').toUpperCase();
  const docCpf = cliente?.cpf ? fmt.cpf(cliente.cpf) : cliente?.cnpj ? fmt.cnpj(cliente.cnpj) : '';

  const enderecoCompleto = () => {
    if (!cliente) return '';
    const p = [];
    if (cliente.logradouro) p.push(cliente.logradouro.toUpperCase());
    if (cliente.numero)     p.push(cliente.numero);
    if (cliente.complemento) p.push(cliente.complemento.toUpperCase());
    if (cliente.bairro)     p.push(`– ${cliente.bairro.toUpperCase()}`);
    if (cliente.cidade)     p.push(`– ${cliente.cidade.toUpperCase()}`);
    if (cliente.uf)         p.push(`/${cliente.uf.toUpperCase()}`);
    if (cliente.cep)        p.push(`– CEP ${cliente.cep}`);
    return p.join(' ');
  };

  return (
    <FormContainer className="print-area">
      <Titulo>
        ANEXO 1 - REQUERIMENTO DE SERVIÇO PARA O PROCESSO DE PRIMEIRO EMPLACAMENTO
      </Titulo>

      <BlocoIdentificacao>
        <LinhaNome>
          <span>Eu, </span>
          <NomeSpan>{nomeCliente}</NomeSpan>
          <span style={{ whiteSpace: 'nowrap' }}> , Identidade</span>
        </LinhaNome>
        <LinhaTexto>
          <span>nº </span>
          <InputLinha value={identidade} onChange={e => setIdentidade(e.target.value.toUpperCase())} style={{ width: '160px' }} placeholder="nº identidade" />
          <span> , CPF nº </span>
          <ValorInline>{docCpf}</ValorInline>
          <span> , Telefone nº </span>
          <ValorInline>{fmt.tel(cliente?.telefone)}</ValorInline>
          <span> , requeiro ao DETRAN/RS a realização do processo de primeiro emplacamento, relativo ao(s) veículo(s) chassi(s) </span>
          <InputLinha value={chassi} onChange={e => setChassi(e.target.value.toUpperCase())} placeholder="chassi(s)" maxLength={20} style={{ width: '180px' }} />
          <span> :</span>
        </LinhaTexto>
      </BlocoIdentificacao>

      <BlocoSeparado>
        <LinhaRestricao>
          <Rotulo>Haverá inclusão ou alteração de restrição financeira?</Rotulo>
          <RadioLabel><input type="radio" name="restrFinPE" onChange={() => setRestrFin('Sim')} /> Sim</RadioLabel>
          <RadioLabel><input type="radio" name="restrFinPE" onChange={() => setRestrFin('Não')} /> Não</RadioLabel>
        </LinhaRestricao>

        <LinhaRestricaoGrid>
          {['Arrendamento', 'Reserva de Domínio', 'Alienação Fiduciária', 'Penhor', 'Comodato', 'Locação'].map(tipo => (
            <RadioLabel key={tipo}>
              <input type="radio" name="tipoRestricaoPE" onChange={() => setTipoRestricao(tipo)} /> {tipo}
            </RadioLabel>
          ))}
        </LinhaRestricaoGrid>

        <LinhaRestricao>
          <Rotulo>O credor é:</Rotulo>
          <RadioLabel><input type="radio" name="tipoCredorPE" onChange={() => setTipoCredor('Pessoa Física')} /> Pessoa Física</RadioLabel>
          <RadioLabel><input type="radio" name="tipoCredorPE" onChange={() => setTipoCredor('Pessoa Jurídica')} /> Pessoa Jurídica</RadioLabel>
        </LinhaRestricao>

        <LinhaRestricao>
          <Rotulo>Agente Financeiro:</Rotulo>
          <InputLinhaCrescente value={agenteFin} onChange={e => setAgenteFin(e.target.value)} />
        </LinhaRestricao>
      </BlocoSeparado>

      <SecaoBox>
        <SecaoTitulo>DADOS DO ADQUIRENTE</SecaoTitulo>
        <SecaoSubtitulo>
          (Preenchimento obrigatório dos campos abaixo, sendo vedada a inclusão de quaisquer dados de terceiros)
        </SecaoSubtitulo>

        <LinhaCampo>
          <Rotulo>Endereço:</Rotulo>
          <CampoValor style={{ flex: 1 }}>{enderecoCompleto()}</CampoValor>
        </LinhaCampo>

        <LinhaCampo>
          <Rotulo>Telefone celular:</Rotulo>
          <CampoValor style={{ width: '120px', flex: 'none' }}>{fmt.tel(cliente?.telefone)}</CampoValor>
          <CheckboxLabel>
            <input type="checkbox" checked={semCelular} onChange={e => setSemCelular(e.target.checked)} />
            Não possui celular
          </CheckboxLabel>
          <Rotulo style={{ marginLeft: '10px' }}>Email:</Rotulo>
          <EmailValor>{semEmail ? '' : (cliente?.email || '').toLowerCase()}</EmailValor>
          <CheckboxLabel>
            <input type="checkbox" checked={semEmail} onChange={e => setSemEmail(e.target.checked)} />
            Não possui Email
          </CheckboxLabel>
        </LinhaCampo>

        <LinhaCampo>
          <Rotulo>Autorizo o DETRAN/RS a enviar por email ou telefone celular informações de interesse junto a este órgão.</Rotulo>
          <RadioLabel><input type="radio" name="autoEmailPE" onChange={() => setAutoEmail('SIM')} /> SIM</RadioLabel>
          <RadioLabel><input type="radio" name="autoEmailPE" onChange={() => setAutoEmail('NÃO')} /> NÃO</RadioLabel>
        </LinhaCampo>
      </SecaoBox>

      <DeclaracaoBox>
        <DeclaracaoTexto>
          <strong>DECLARO, sob as penas da lei e sem prejuízo de demais sanções administrativas/cíveis/criminais, que:</strong>
        </DeclaracaoTexto>
        <DeclaracaoTexto style={{ marginTop: '8px' }}>
          <strong>a)</strong> o(s) veículo(s) são novos (Zero quilômetro) e está(ão) fisicamente de acordo com o pré-cadastro na
          Base Indice Nacional – BIN da SENATRAN, Nota(s) Fiscal(is), bem como do Certificado de
          Adequação a Legislação de Trânsito – CAT e do Certificado de Capacitação Técnica – CCT, quando exigível(is);
        </DeclaracaoTexto>
        <DeclaracaoTexto style={{ marginTop: '6px' }}>
          <strong>b)</strong> estou ciente de que no caso de qualquer alteração do veículo em relação ao pré-cadastro e/ou com
          o CAT/CCT quando solicitados, é de minha responsabilidade informar ao Centro de Registro de
          Veículos Automotores – CRVA, para correção/alteração das informações do veículo antes do
          primeiro emplacamento, para fins de adequação à legislação de Trânsito.
        </DeclaracaoTexto>
        <DeclaracaoTexto style={{ marginTop: '6px' }}>
          <strong>c)</strong> os documentos entregues ao DETRAN/RS para o serviço requerido são todos autênticos e,
          quando cópias, condizem com o original, e que sou responsável por providenciar a estampagem e
          instalação da(s) placa(s) no veículo para o qual a estampagem foi autorizada.
        </DeclaracaoTexto>
      </DeclaracaoBox>

      <AssinaturaContainer>
        <MunicipioLinha>
          Município: <strong>{(cidadeEmpresa || '').toUpperCase()}</strong>, {dataAtual.dia} de {dataAtual.mes} de {dataAtual.ano}.
        </MunicipioLinha>
        <EspacoAssinatura />
        <AssinaturaLinha />
        <AssinaturaRotulo>Assinatura do Responsável</AssinaturaRotulo>
      </AssinaturaContainer>
    </FormContainer>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// FORMULÁRIO 3 — REQUERIMENTO PARA SOLICITAÇÃO DE 2ª VIA DE CRV/ATPV-e
// ═════════════════════════════════════════════════════════════════════════════

const FormSegundaViaCrv = ({ cliente, placaInicial, chassiInicial, marcaInicial = '', cidadeEmpresa, onVeiculoChange }) => {
  const [placa, setPlaca]           = useState(placaInicial);
  const [chassi, setChassi]         = useState(chassiInicial);
  const [identidade, setIdentidade] = useState(cliente?.identidade || '');
  const [marca, setMarca]           = useState(marcaInicial);
  const [solicitacao, setSolicitacao] = useState('');
  const [numeroCrv, setNumeroCrv]   = useState('');
  const [dataEmissao, setDataEmissao] = useState('');
  const [justificativa, setJustificativa] = useState('');

  useEffect(() => { setPlaca(placaInicial); setChassi(chassiInicial); }, [placaInicial, chassiInicial]);
  useEffect(() => { setIdentidade(cliente?.identidade || ''); }, [cliente]);
  useEffect(() => { if (onVeiculoChange) onVeiculoChange({ placa, chassi, marcaModelo: marca }); }, [placa, chassi, marca, onVeiculoChange]);

  const hoje = new Date();
  const dataAtual = {
    dia: String(hoje.getDate()).padStart(2, '0'),
    mes: MESES[hoje.getMonth()],
    ano: hoje.getFullYear(),
  };

  const nomeCliente = (cliente?.nome || cliente?.razaoSocial || '').toUpperCase();

  return (
    <FormContainer className="print-area">
      <Titulo>
        ANEXO 1 – REQUERIMENTO PARA SOLICITAÇÃO DE 2ª VIA DE CRV/ATPV-e OU VALIDAÇÃO DE CRV
      </Titulo>

      <BlocoIdentificacao>
        <LinhaTexto>
          <span>Eu, </span>
          <NomeSpan>{nomeCliente}</NomeSpan>
          <span> , identidade nº </span>
          <InputLinha value={identidade} onChange={e => setIdentidade(e.target.value.toUpperCase())} style={{ width: '130px' }} placeholder="nº identidade" />
          <span> , proprietário do veículo marca </span>
          <InputLinha value={marca} onChange={e => setMarca(e.target.value.toUpperCase())} style={{ width: '140px' }} placeholder="marca/modelo" />
          <span> , placas </span>
          <InputLinha value={placa} onChange={e => setPlaca(e.target.value.toUpperCase())} style={{ width: '75px' }} placeholder="ABC-1234" maxLength={8} />
          <span> , chassi </span>
          <InputLinha value={chassi} onChange={e => setChassi(e.target.value.toUpperCase())} style={{ width: '175px' }} placeholder="chassi" maxLength={20} />
          <span> , requeiro:</span>
        </LinhaTexto>
      </BlocoIdentificacao>

      <SecaoBox style={{ marginTop: '14px' }}>
        <LinhaRestricao style={{ marginBottom: '6px' }}>
          <RadioLabel>
            <input type="radio" name="solicitacaoCrv" onChange={() => setSolicitacao('2via')} />
            Solicitação de 2ª via de CRV/ATPV-e;
          </RadioLabel>
        </LinhaRestricao>
        <LinhaRestricao>
          <RadioLabel>
            <input type="radio" name="solicitacaoCrv" onChange={() => setSolicitacao('validacao')} />
            Validação do CRV número
          </RadioLabel>
          <InputLinha value={numeroCrv} onChange={e => setNumeroCrv(e.target.value)} style={{ width: '160px' }} placeholder="nº do CRV" />
          <span style={{ fontSize: '9.5pt' }}> , para efeitos de transferência de propriedade do veículo.</span>
        </LinhaRestricao>
      </SecaoBox>

      <SecaoBox style={{ marginTop: '10px' }}>
        <SecaoSubtitulo style={{ marginBottom: '10px', fontStyle: 'normal', fontWeight: '600' }}>
          Justificativa da não apresentação do CRV/ATPV-e anterior válido emitido pelo DETRAN/RS em
          <InputLinha value={dataEmissao} onChange={e => setDataEmissao(e.target.value)} style={{ width: '90px', marginLeft: '6px' }} placeholder="dd/mm/aaaa" />
          :
        </SecaoSubtitulo>

        <LinhaRestricao style={{ marginBottom: '5px' }}>
          <RadioLabel><input type="radio" name="justCrv" onChange={() => setJustificativa('nao_recebido')} /> não recebido;</RadioLabel>
        </LinhaRestricao>
        <LinhaRestricao style={{ marginBottom: '5px' }}>
          <RadioLabel><input type="radio" name="justCrv" onChange={() => setJustificativa('inutilizado')} /> inutilizado (deverá acompanhar o documento no estado em que se encontra);</RadioLabel>
        </LinhaRestricao>
        <LinhaRestricao style={{ marginBottom: '5px' }}>
          <RadioLabel><input type="radio" name="justCrv" onChange={() => setJustificativa('perdido')} /> perdido/extraviado (deverá acompanhar Boletim de Ocorrência Policial do fato);</RadioLabel>
        </LinhaRestricao>
        <LinhaRestricao>
          <RadioLabel><input type="radio" name="justCrv" onChange={() => setJustificativa('furtado')} /> furtado/roubado (deverá acompanhar Boletim de Ocorrência Policial do fato).</RadioLabel>
        </LinhaRestricao>
      </SecaoBox>

      <DeclaracaoBox style={{ marginTop: '10px' }}>
        <DeclaracaoTexto>
          O proprietário declarante responsabiliza-se civil e criminalmente pela veracidade das informações prestadas.
        </DeclaracaoTexto>
      </DeclaracaoBox>

      <AssinaturaContainerCentralizada>
        <MunicipioLinha>
          {(cidadeEmpresa || '________________').toUpperCase()}, {dataAtual.dia} de {dataAtual.mes} de {dataAtual.ano}.
        </MunicipioLinha>
        <EspacoAssinatura />
        <AssinaturaLinha />
        <AssinaturaRotulo>
          Assinatura do Proprietário/Arrendatário/Procurador com firma reconhecida por autenticidade
          no caso do não comparecimento do signatário ao Centro (ou assinado com assinatura eletrônica, conforme POP 47)
        </AssinaturaRotulo>
      </AssinaturaContainerCentralizada>
    </FormContainer>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// FORMULÁRIO 4 — REQUERIMENTO PARA BAIXA DEFINITIVA
// ═════════════════════════════════════════════════════════════════════════════

const FormBaixaDefinitiva = ({ cliente, placaInicial, chassiInicial, marcaInicial = '', cidadeEmpresa, onVeiculoChange }) => {
  const [placa, setPlaca]           = useState(placaInicial);
  const [chassi, setChassi]         = useState(chassiInicial);
  const [marca, setMarca]           = useState(marcaInicial);
  const [motivoBaixa, setMotivoBaixa] = useState('');
  const [docsPerdidos, setDocsPerdidos] = useState([]);
  const [identidade, setIdentidade] = useState(cliente?.identidade || '');

  useEffect(() => { setPlaca(placaInicial); setChassi(chassiInicial); }, [placaInicial, chassiInicial]);
  useEffect(() => { setIdentidade(cliente?.identidade || ''); }, [cliente]);
  useEffect(() => { if (onVeiculoChange) onVeiculoChange({ placa, chassi, marcaModelo: marca }); }, [placa, chassi, marca, onVeiculoChange]);

  const hoje = new Date();
  const dataAtual = {
    dia: String(hoje.getDate()).padStart(2, '0'),
    mes: MESES[hoje.getMonth()],
    ano: hoje.getFullYear(),
  };

  const nomeCliente = (cliente?.nome || cliente?.razaoSocial || '').toUpperCase();
  const docCpf = cliente?.cpf ? fmt.cpf(cliente.cpf) : cliente?.cnpj ? fmt.cnpj(cliente.cnpj) : '';

  const toggleDocPerdido = (doc) =>
    setDocsPerdidos(prev => prev.includes(doc) ? prev.filter(d => d !== doc) : [...prev, doc]);

  const motivosBaixa = [
    'Veículo irrecuperável',
    'Veículo definitivamente desmontado',
    'Veículo sinistrado com laudo de perda total',
    'Veículo vendido ou leiloado como sucata',
    'Baixa de Veículo para outro País conforme decalque ou fotografia da gravação do número do chassi, em anexo.',
  ];

  return (
    <FormContainer className="print-area">
      <Titulo>
        ANEXO 1 - REQUERIMENTO PARA BAIXA DEFINITIVA (CONFORME RESOLUÇÃO CONTRAN Nº 967/22)
      </Titulo>

      <BlocoIdentificacao>
        <LinhaTexto>
          <span>Eu, </span>
          <NomeSpan>{nomeCliente}</NomeSpan>
          <span> , portador do RG nº: </span>
          <InputLinha value={identidade} onChange={e => setIdentidade(e.target.value.toUpperCase())} style={{ width: '130px' }} placeholder="nº RG" />
          <span> e do CPF nº: </span>
          <ValorInline>{docCpf}</ValorInline>
          <span> , proprietário/responsável do veículo placa </span>
          <InputLinha value={placa} onChange={e => setPlaca(e.target.value.toUpperCase())} style={{ width: '75px' }} placeholder="ABC-1234" maxLength={8} />
          <span> , chassi </span>
          <InputLinha value={chassi} onChange={e => setChassi(e.target.value.toUpperCase())} style={{ width: '175px' }} placeholder="chassi" maxLength={20} />
        </LinhaTexto>
        <LinhaTexto>
          <span>marca/modelo </span>
          <InputLinha value={marca} onChange={e => setMarca(e.target.value.toUpperCase())} style={{ width: '200px' }} placeholder="marca/modelo" />
          <span> , requeiro a BAIXA DEFINITIVA do mesmo, sob pena de responsabilidade civil e criminal, devido tratar-se de:</span>
        </LinhaTexto>
      </BlocoIdentificacao>

      <SecaoBox style={{ marginTop: '14px' }}>
        {motivosBaixa.map(motivo => (
          <LinhaRestricao key={motivo} style={{ marginBottom: '6px' }}>
            <RadioLabel>
              <input type="radio" name="motivoBaixa" onChange={() => setMotivoBaixa(motivo)} />
              {motivo}
            </RadioLabel>
          </LinhaRestricao>
        ))}

        <div style={{ marginTop: '14px', fontSize: '9.5pt', fontWeight: '500' }}>
          Bem como, declaro que perdi/extraviei os seguintes documentos/elementos:
        </div>

        <div style={{ marginLeft: '20px', marginTop: '8px' }}>
          {['CRV (se veículo verde)', 'Placas', 'Plaquetas', 'Lacre'].map(doc => (
            <LinhaRestricao key={doc} style={{ marginBottom: '5px' }}>
              <CheckboxLabel>
                <input type="checkbox" checked={docsPerdidos.includes(doc)} onChange={() => toggleDocPerdido(doc)} />
                {doc}
              </CheckboxLabel>
            </LinhaRestricao>
          ))}
        </div>
      </SecaoBox>

      <AssinaturaContainerCentralizada>
        <MunicipioLinha>
          {(cidadeEmpresa || '________________').toUpperCase()}, {dataAtual.dia} de {dataAtual.mes} de {dataAtual.ano}.
        </MunicipioLinha>
        <EspacoAssinatura />
        <AssinaturaLinha />
        <AssinaturaRotulo>
          Requerente<br />
          <span style={{ fontSize: '8pt', fontWeight: 'normal' }}>
            (Reconhecer firma do proprietário ou seu representante, por autenticidade em caso de não comparecimento ao CRVA,
            ou assinado com assinatura eletrônica, conforme POP 47)
          </span>
        </AssinaturaRotulo>
      </AssinaturaContainerCentralizada>
    </FormContainer>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// FORMULÁRIO 5 — DECLARAÇÃO DE RESIDÊNCIA
// ═════════════════════════════════════════════════════════════════════════════

const FormDeclaracaoResidencia = ({ cliente, cidadeEmpresa }) => {
  const [identidade, setIdentidade]   = useState(cliente?.identidade || '');
  const [logradouro, setLogradouro]   = useState(cliente?.logradouro || '');
  const [numero, setNumero]           = useState(cliente?.numero || '');
  const [complemento, setComplemento] = useState(cliente?.complemento || '');
  const [bairro, setBairro]           = useState(cliente?.bairro || '');
  const [cidade, setCidade]           = useState(cliente?.cidade || '');
  const [uf, setUf]                   = useState(cliente?.uf || '');
  const [cep, setCep]                 = useState(cliente?.cep || '');

  useEffect(() => {
    setIdentidade(cliente?.identidade || '');
    setLogradouro(cliente?.logradouro || '');
    setNumero(cliente?.numero || '');
    setComplemento(cliente?.complemento || '');
    setBairro(cliente?.bairro || '');
    setCidade(cliente?.cidade || '');
    setUf(cliente?.uf || '');
    setCep(cliente?.cep || '');
  }, [cliente]);

  const hoje = new Date();
  const dataAtual = {
    dia: String(hoje.getDate()).padStart(2, '0'),
    mes: MESES[hoje.getMonth()],
    ano: hoje.getFullYear(),
  };

  const nomeCliente = (cliente?.nome || cliente?.razaoSocial || '').toUpperCase();
  const docCpf = cliente?.cpf ? fmt.cpf(cliente.cpf) : cliente?.cnpj ? fmt.cnpj(cliente.cnpj) : '';

  return (
    <FormContainer className="print-area">
      <Titulo>ANEXO 1 - DECLARAÇÃO DE RESIDÊNCIA</Titulo>

      <BlocoIdentificacao>
        <LinhaTexto>
          <span>Eu, </span>
          <NomeSpan>{nomeCliente}</NomeSpan>
          <span> , portador do CPF </span>
          <ValorInline>{docCpf}</ValorInline>
          <span> , da cédula de identidade nº </span>
          <InputLinha value={identidade} onChange={e => setIdentidade(e.target.value.toUpperCase())} style={{ width: '150px' }} placeholder="nº identidade" />
          <span> , telefone celular nº </span>
          <ValorInline>{fmt.tel(cliente?.telefone)}</ValorInline>
          <span> , email </span>
          <EmailValor>{(cliente?.email || '').toLowerCase()}</EmailValor>
          <span> na impossibilidade de apresentar um dos comprovantes previstos na Portaria DETRAN/RS nº 183/25, para o fim de atos de registro de veículo
            junto ao Centro de Registros de Veículos Automotores, declaro sob as penas da lei, que meu endereço
            é na rua/av. </span>
          <InputLinha value={logradouro} onChange={e => setLogradouro(e.target.value.toUpperCase())} style={{ width: '220px' }} placeholder="logradouro" />
          <span> , número </span>
          <InputLinha value={numero} onChange={e => setNumero(e.target.value)} style={{ width: '60px' }} placeholder="nº" />
          <span> , complemento </span>
          <InputLinha value={complemento} onChange={e => setComplemento(e.target.value.toUpperCase())} style={{ width: '100px' }} placeholder="compl." />
          <span> , bairro </span>
          <InputLinha value={bairro} onChange={e => setBairro(e.target.value.toUpperCase())} style={{ width: '140px' }} placeholder="bairro" />
          <span> , na cidade de </span>
          <InputLinha value={cidade} onChange={e => setCidade(e.target.value.toUpperCase())} style={{ width: '160px' }} placeholder="cidade" />
          <span> , UF </span>
          <InputLinha value={uf} onChange={e => setUf(e.target.value.toUpperCase())} style={{ width: '35px' }} placeholder="UF" maxLength={2} />
          <span> , CEP </span>
          <InputLinha value={cep} onChange={e => setCep(e.target.value)} style={{ width: '90px' }} placeholder="00000-000" maxLength={9} />
          <span> .</span>
        </LinhaTexto>
      </BlocoIdentificacao>

      <AssinaturaContainerCentralizada style={{ marginTop: '36px' }}>
        <MunicipioLinha>
          {(cidadeEmpresa || '________________').toUpperCase()}, {dataAtual.dia} de {dataAtual.mes} de {dataAtual.ano}.
        </MunicipioLinha>
        <EspacoAssinatura />
        <AssinaturaLinha />
        <AssinaturaRotulo>Proprietário / Adquirente / Procurador com poder expresso</AssinaturaRotulo>
      </AssinaturaContainerCentralizada>

      <AssinaturaContainerCentralizada style={{ marginTop: '28px' }}>
        <div style={{ fontSize: '9.5pt', marginBottom: '4px' }}>
          Atesto que a assinatura acima foi firmada na minha presença.
        </div>
        <EspacoAssinatura style={{ height: '40px', width: '55%' }} />
        <AssinaturaRotulo>Assinatura e carimbo do IVD/DDT</AssinaturaRotulo>
      </AssinaturaContainerCentralizada>
    </FormContainer>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// FORMULÁRIO 6 — REQUERIMENTO DE PREENCHIMENTO DA ATPV-e (Anexo 2 do POP 02)
// Texto trazido dos Documentos Complementares do Truck Control. O cliente selecionado é o
// proprietário vendedor; o adquirente é buscado no cadastro (ou pela Receita, se CNPJ) e pode ser
// salvo/atualizado no cadastro de clientes pelo painel acima da folha (que não sai na impressão).
// ═════════════════════════════════════════════════════════════════════════════

const FormPreenchimentoAtpve = ({ cliente, placaInicial, chassiInicial, onVeiculoChange, adquirenteInicial, valorInicial, onBloqueioImpressaoChange }) => {
  const { API_BASE_URL } = config;
  const { addToast } = useToast();
  const [placa, setPlaca]           = useState(placaInicial);
  const [chassi, setChassi]         = useState(chassiInicial);
  const [identidade, setIdentidade] = useState(cliente?.identidade || '');
  const [orgaoEmissor, setOrgaoEmissor] = useState('');
  const [emailVendedor, setEmailVendedor] = useState(cliente?.email || '');
  // Valor da venda informado no modal do adquirente; sem ele a página não libera imprimir/salvar PDF
  const [valor, setValor]           = useState(valorInicial != null && valorInicial > 0 ? valorInicial : null);
  const valorOk = valor != null && valor > 0;
  useEffect(() => {
    if (onBloqueioImpressaoChange) onBloqueioImpressaoChange(valorOk ? null : 'Informe o valor da venda no requerimento para liberar a impressão.');
  }, [valorOk, onBloqueioImpressaoChange]);

  // Adquirente (formato de pessoa dos documentos) e o que está gravado no cadastro, para saber se mudou.
  // Vem preenchido quando foi escolhido no modal do adquirente, antes de abrir o requerimento.
  const [adquirente, setAdquirente] = useState(() => (adquirenteInicial ? clienteParaPessoa(adquirenteInicial) : pessoaVazia()));
  const [origemAdquirente, setOrigemAdquirente] = useState(() => (adquirenteInicial ? clienteParaPessoa(adquirenteInicial) : null));
  const [buscandoAdq, setBuscandoAdq] = useState(false);
  const [salvandoAdq, setSalvandoAdq] = useState(false);

  useEffect(() => { setPlaca(placaInicial); setChassi(chassiInicial); }, [placaInicial, chassiInicial]);
  useEffect(() => { setIdentidade(cliente?.identidade || ''); setEmailVendedor(cliente?.email || ''); }, [cliente]);
  useEffect(() => { if (onVeiculoChange) onVeiculoChange({ placa, chassi }); }, [placa, chassi, onVeiculoChange]);

  const hoje = new Date();
  const dataCurtaHoje = `${String(hoje.getDate()).padStart(2, '0')}/${String(hoje.getMonth() + 1).padStart(2, '0')}/${hoje.getFullYear()}`;

  const nomeCliente = (cliente?.nome || cliente?.razaoSocial || '').toUpperCase();
  const docVendedor = cliente?.cpf ? fmt.cpf(cliente.cpf) : cliente?.cnpj ? fmt.cnpj(cliente.cnpj) : '';
  const rotuloDocVendedor = cliente?.cpf ? 'CPF' : cliente?.cnpj ? 'CNPJ' : 'CPF/CNPJ';

  const setAdq = (campo) => (e) => {
    const v = e.target.value;
    setAdquirente((p) => ({ ...p, [campo]: campo === 'email' ? v : v.toUpperCase() }));
  };

  // CEP completo: completa o endereço do adquirente pelo ViaCEP
  const mudarCepAdquirente = async (e) => {
    const cep = formatarCep(e.target.value);
    setAdquirente((p) => ({ ...p, cep }));
    if (cep.replace(/\D/g, '').length === 8) {
      const endereco = await buscarCep(cep);
      if (endereco) setAdquirente((p) => ({ ...p, ...endereco, cidade: endereco.cidade.toUpperCase(), logradouro: endereco.logradouro.toUpperCase(), bairro: endereco.bairro.toUpperCase() }));
    }
  };

  // Busca o adquirente no cadastro pelo CPF/CNPJ; se não houver e for CNPJ, consulta a Receita
  const buscarAdquirente = async () => {
    const doc = removerMascaraCPFCNPJ(adquirente.cpfCnpj);
    if (doc.length !== 11 && doc.length !== 14) {
      addToast('warning', 'Digite o CPF ou CNPJ completo do adquirente.');
      return;
    }
    setBuscandoAdq(true);
    try {
      const existente = await buscarClientePorDocumento(doc);
      if (existente) {
        const pessoa = clienteParaPessoa(existente);
        setOrigemAdquirente(pessoa);
        // Cadastro antigo/incompleto com CNPJ: completa só os campos em branco pela Receita (o resto fica como está)
        const camposCnpj = ['email', 'cep', 'logradouro', 'numero', 'complemento', 'bairro', 'cidade', 'uf'];
        if (doc.length === 14 && camposCnpj.some((c) => !String(pessoa[c] ?? '').trim())) {
          const { dados } = await consultarCnpj(doc, API_BASE_URL, { headers: { Authorization: `Bearer ${localStorage.getItem('jwtToken')}` } });
          if (dados) {
            const maiusculos = Object.fromEntries(Object.entries(dados).map(([c, v]) => [c, c === 'email' ? (v || '') : (v || '').toUpperCase()]));
            const { novo, completados } = completarComDadosCnpj(pessoa, maiusculos);
            novo.cep = formatarCep(novo.cep);
            setAdquirente(novo);
            addToast('success', completados.length > 0
              ? `Adquirente encontrado no cadastro: ${existente.nome}. Completado pelo CNPJ: ${completados.join(', ')}.`
              : `Adquirente encontrado no cadastro: ${existente.nome}.`);
            return;
          }
        }
        setAdquirente(pessoa);
        addToast('success', `Adquirente encontrado no cadastro: ${existente.nome}.`);
        return;
      }
      setOrigemAdquirente(null);
      setAdquirente((p) => ({ ...p, idCliente: null }));
      if (doc.length === 14) {
        const { dados, erro } = await consultarCnpj(doc, API_BASE_URL, { headers: { Authorization: `Bearer ${localStorage.getItem('jwtToken')}` } });
        if (dados) {
          setAdquirente((p) => {
            const novo = { ...p };
            Object.keys(dados).forEach((c) => { novo[c] = c === 'email' ? (dados[c] || '') : (dados[c] || '').toUpperCase(); });
            novo.cep = formatarCep(novo.cep);
            return novo;
          });
          addToast('success', 'Adquirente não cadastrado: dados preenchidos pelo CNPJ (Receita Federal). Confira antes de imprimir.');
        } else {
          addToast('warning', `Adquirente não cadastrado. ${erro}`);
        }
      } else {
        addToast('warning', 'Adquirente não cadastrado: preencha os dados na folha.');
      }
    } finally {
      setBuscandoAdq(false);
    }
  };

  const situacaoAdquirente = situacaoCadastro({
    vinculado: !!adquirente.idCliente, origem: origemAdquirente, atual: adquirente,
    campos: ['nome', 'cpfCnpj', 'email', 'cep', 'logradouro', 'numero', 'complemento', 'bairro', 'cidade', 'uf'],
    obrigatorios: ['nome', 'cpfCnpj'], rotuloNovo: 'Adquirente ainda não cadastrado: pode ser salvo no cadastro de clientes.',
    camposFixos: ['cpfCnpj'],
    rotulosCampo: { nome: 'nome', email: 'e-mail', cep: 'CEP', logradouro: 'logradouro', numero: 'número', complemento: 'complemento', bairro: 'bairro', cidade: 'município', uf: 'UF' }
  });

  // Salva/atualiza o adquirente no cadastro de clientes (campos em branco não apagam nada)
  const salvarAdquirente = async () => {
    const doc = removerMascaraCPFCNPJ(adquirente.cpfCnpj);
    if (!adquirente.nome.trim() || (doc.length !== 11 && doc.length !== 14)) {
      addToast('warning', 'Preencha ao menos o nome e o CPF/CNPJ completo do adquirente.');
      return;
    }
    setSalvandoAdq(true);
    try {
      const existente = adquirente.idCliente ? { idCliente: adquirente.idCliente } : await buscarClientePorDocumento(doc);
      const salvo = existente ? await complementarCliente(existente.idCliente, adquirente) : await criarCliente(adquirente);
      const pessoa = clienteParaPessoa(salvo);
      setAdquirente(pessoa);
      setOrigemAdquirente(pessoa);
      addToast('success', existente ? 'Cadastro do adquirente atualizado.' : 'Adquirente cadastrado com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar adquirente:', error);
      addToast('error', 'Erro ao salvar o adquirente: ' + (error.response?.data?.message || error.message));
    } finally {
      setSalvandoAdq(false);
    }
  };


  return (
    <>
      <PainelAdquirente className="no-print">
        <strong>Adquirente (comprador)</strong>
        <div className="linha">
          <input
            value={mascaraCpfCnpj(adquirente.cpfCnpj)}
            onChange={(e) => { setAdquirente((p) => ({ ...p, cpfCnpj: removerMascaraCPFCNPJ(e.target.value).slice(0, 14) })); }}
            onKeyPress={(e) => e.key === 'Enter' && buscarAdquirente()}
            placeholder="CPF ou CNPJ do adquirente"
            disabled={!!adquirente.idCliente}
          />
          <button type="button" onClick={buscarAdquirente} disabled={buscandoAdq || !!adquirente.idCliente}>
            {buscandoAdq ? 'Buscando...' : 'Buscar adquirente'}
          </button>
          {adquirente.idCliente && (
            <button type="button" className="secundario" onClick={() => { setAdquirente(pessoaVazia()); setOrigemAdquirente(null); }}>
              Trocar adquirente
            </button>
          )}
        </div>
        <span className="dica">Busca no cadastro de clientes; se não estiver cadastrado e for CNPJ, consulta a Receita Federal. Este painel não sai na impressão.</span>
        <BarraCadastro situacao={situacaoAdquirente} salvando={salvandoAdq} onSalvar={salvarAdquirente} rotulo="Salvar adquirente no cadastro" />
      </PainelAdquirente>

      <FormContainer className="print-area">
        <Titulo>ANEXO 2 - REQUERIMENTO DE PREENCHIMENTO DA ATPV-e</Titulo>

        <BlocoIdentificacao>
          <LinhaTexto>
            <span>Eu, </span>
            <NomeSpan>{nomeCliente}</NomeSpan>
            <span> , {rotuloDocVendedor} nº </span>
            <ValorInline>{docVendedor}</ValorInline>
            <span> , RG nº </span>
            <InputLinha value={identidade} onChange={e => setIdentidade(e.target.value.toUpperCase())} style={{ width: '130px' }} placeholder="nº identidade" />
            <span> , órgão emissor </span>
            <InputLinha value={orgaoEmissor} onChange={e => setOrgaoEmissor(e.target.value.toUpperCase())} style={{ width: '80px' }} placeholder="SSP/RS" />
            <span> , requeiro ao DETRAN/RS, o preenchimento da ATPV-e, relativo ao veículo placa </span>
            <InputLinha value={placa} onChange={e => setPlaca(e.target.value.toUpperCase())} style={{ width: '75px' }} placeholder="ABC-1234" maxLength={8} />
            <span> , chassi </span>
            <InputLinha value={chassi} onChange={e => setChassi(e.target.value.toUpperCase())} style={{ width: '175px' }} placeholder="chassi" maxLength={20} />
            <span> .</span>
          </LinhaTexto>
        </BlocoIdentificacao>

        <SecaoBox style={{ marginTop: '14px' }}>
          <SecaoTitulo style={{ marginBottom: '6px' }}>PROPRIETÁRIO VENDEDOR</SecaoTitulo>
          <LinhaCampo>
            <Rotulo>E-MAIL:</Rotulo>
            <InputLinhaCrescente value={emailVendedor} onChange={e => setEmailVendedor(e.target.value)} placeholder="e-mail do vendedor" style={{ fontWeight: 700 }} />
          </LinhaCampo>
        </SecaoBox>

        <SecaoBox>
          <SecaoTitulo style={{ marginBottom: '6px' }}>IDENTIFICAÇÃO DO ADQUIRENTE</SecaoTitulo>
          <LinhaCampo>
            <Rotulo>CPF/CNPJ:</Rotulo>
            <InputLinha
              value={mascaraCpfCnpj(adquirente.cpfCnpj)}
              onChange={(e) => setAdquirente((p) => ({ ...p, cpfCnpj: removerMascaraCPFCNPJ(e.target.value).slice(0, 14) }))}
              style={{ width: '170px' }} placeholder="CPF/CNPJ" disabled={!!adquirente.idCliente}
            />
          </LinhaCampo>
          <LinhaCampo>
            <Rotulo>NOME:</Rotulo>
            <InputLinhaCrescente value={adquirente.nome} onChange={setAdq('nome')} placeholder="nome do adquirente" style={{ fontWeight: 700, textTransform: 'uppercase' }} />
          </LinhaCampo>
          <LinhaCampo>
            <Rotulo>E-MAIL:</Rotulo>
            <InputLinhaCrescente value={adquirente.email} onChange={setAdq('email')} placeholder="e-mail do adquirente" style={{ fontWeight: 700 }} />
          </LinhaCampo>
        </SecaoBox>

        <SecaoBox>
          <SecaoTitulo style={{ marginBottom: '6px' }}>ENDEREÇO DO ADQUIRENTE</SecaoTitulo>
          <LinhaCampo>
            <Rotulo>CEP:</Rotulo>
            <InputLinha value={adquirente.cep} onChange={mudarCepAdquirente} style={{ width: '90px' }} placeholder="00000-000" maxLength={9} />
            <Rotulo style={{ marginLeft: '14px' }}>UF:</Rotulo>
            <InputLinha value={adquirente.uf} onChange={setAdq('uf')} style={{ width: '35px' }} placeholder="UF" maxLength={2} />
            <Rotulo style={{ marginLeft: '14px' }}>MUNICÍPIO:</Rotulo>
            <InputLinhaCrescente value={adquirente.cidade} onChange={setAdq('cidade')} placeholder="município" style={{ fontWeight: 700, textTransform: 'uppercase' }} />
          </LinhaCampo>
          <LinhaCampo>
            <Rotulo>LOGRADOURO:</Rotulo>
            <InputLinhaCrescente value={adquirente.logradouro} onChange={setAdq('logradouro')} placeholder="logradouro" style={{ fontWeight: 700, textTransform: 'uppercase' }} />
            <Rotulo style={{ marginLeft: '14px' }}>Nº:</Rotulo>
            <InputLinha value={adquirente.numero} onChange={setAdq('numero')} style={{ width: '60px' }} placeholder="nº" />
          </LinhaCampo>
          <LinhaCampo>
            <Rotulo>COMPLEMENTO:</Rotulo>
            <InputLinha value={adquirente.complemento} onChange={setAdq('complemento')} style={{ width: '160px' }} placeholder="complemento" />
            <Rotulo style={{ marginLeft: '14px' }}>BAIRRO:</Rotulo>
            <InputLinhaCrescente value={adquirente.bairro} onChange={setAdq('bairro')} placeholder="bairro" style={{ fontWeight: 700, textTransform: 'uppercase' }} />
          </LinhaCampo>
        </SecaoBox>

        <LinhaCampo style={{ marginTop: '10px', fontWeight: 700 }}>
          <Rotulo style={{ fontWeight: 700 }}>VALOR: R$</Rotulo>
          <NumericFormat
            value={valor ?? ''}
            onValueChange={(v, info) => { if (info.source === 'event') setValor(v.floatValue ?? null); }}
            thousandSeparator="." decimalSeparator="," decimalScale={2} fixedDecimalScale
            placeholder="0,00"
            customInput={InputLinha}
            style={{ width: '120px', ...(valorOk ? {} : { borderBottom: '2px solid #e74c3c', background: '#fdecea' }) }}
          />
          {!valorOk && (
            <span className="no-print" style={{ color: '#e74c3c', fontSize: '9pt', fontWeight: 600 }}>
              obrigatório para imprimir
            </span>
          )}
        </LinhaCampo>

        <DeclaracaoBox style={{ marginTop: '12px', background: '#f2f2f2' }}>
          <DeclaracaoTexto style={{ fontWeight: 700 }}>
            DECLARO, sob as penas da lei e sem prejuízo de demais sanções administrativas/cíveis/criminais, que os
            documentos entregues ao DETRAN/RS para o serviço requerido são todos autênticos e, quando cópias, condizem com o original.
          </DeclaracaoTexto>
        </DeclaracaoBox>

        <AssinaturaContainerCentralizada>
          <MunicipioLinha>Data: {dataCurtaHoje}</MunicipioLinha>
          <EspacoAssinatura />
          <AssinaturaLinha />
          <AssinaturaRotulo>ASSINATURA DO VENDEDOR/REPRESENTANTE LEGAL</AssinaturaRotulo>
        </AssinaturaContainerCentralizada>
      </FormContainer>
    </>
  );
};

// Painel de busca/cadastro do adquirente (fora da folha: não é impresso nem vai para o PDF)
const PainelAdquirente = styled.div`
  width: 210mm;
  max-width: 100%;
  margin: 0 auto 14px;
  box-sizing: border-box;
  background: #fff;
  border: 1px solid rgba(0, 128, 128, 0.25);
  border-radius: 10px;
  padding: 12px 14px;
  font-family: Arial, Helvetica, sans-serif;
  display: flex;
  flex-direction: column;
  gap: 8px;
  strong { font-size: 14px; color: #2c3e50; }
  .linha { display: flex; gap: 8px; flex-wrap: wrap; }
  .linha input { flex: 1; min-width: 200px; padding: 8px 10px; border: 1px solid #ccd; border-radius: 6px; font-size: 14px; }
  .linha button { background: #008080; color: #fff; border: none; border-radius: 6px; padding: 8px 14px; cursor: pointer; font-weight: 600; }
  .linha button.secundario { background: #fff; color: #008080; border: 1px solid #008080; }
  .linha button:disabled { opacity: 0.6; cursor: not-allowed; }
  .dica { font-size: 12px; color: #7f8c8d; }
`;

// ═════════════════════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL — roteador de formulários
// ═════════════════════════════════════════════════════════════════════════════

// onVeiculoChange({ placa, chassi, marcaModelo }): avisa a página dos dados do veículo como estão na folha
// (inclusive editados aqui), para gravá-los no cadastro de veículos ao imprimir/salvar o PDF.
// onBloqueioImpressaoChange(motivo | null): o formulário avisa quando falta algo obrigatório para imprimir (ATPV-e: valor)
const RequerimentoDetranForm = ({ cliente, tipoRequerimento, placaInicial = '', chassiInicial = '', marcaInicial = '', cidadeEmpresa = '', onVeiculoChange, adquirenteInicial = null, valorInicial = null, onBloqueioImpressaoChange }) => {
  return (
    <>
      <PrintStyles />
      {tipoRequerimento === 'SERVICO_DECLARACAO' && (
        <FormServicoDeclaracao cliente={cliente} placaInicial={placaInicial} chassiInicial={chassiInicial} cidadeEmpresa={cidadeEmpresa} onVeiculoChange={onVeiculoChange} />
      )}
      {tipoRequerimento === 'PRIMEIRO_EMPLACAMENTO' && (
        <FormPrimeiroEmplacamento cliente={cliente} chassiInicial={chassiInicial} cidadeEmpresa={cidadeEmpresa} onVeiculoChange={onVeiculoChange} />
      )}
      {tipoRequerimento === 'SEGUNDA_VIA_CRV' && (
        <FormSegundaViaCrv cliente={cliente} placaInicial={placaInicial} chassiInicial={chassiInicial} marcaInicial={marcaInicial} cidadeEmpresa={cidadeEmpresa} onVeiculoChange={onVeiculoChange} />
      )}
      {tipoRequerimento === 'BAIXA_DEFINITIVA' && (
        <FormBaixaDefinitiva cliente={cliente} placaInicial={placaInicial} chassiInicial={chassiInicial} marcaInicial={marcaInicial} cidadeEmpresa={cidadeEmpresa} onVeiculoChange={onVeiculoChange} />
      )}
      {tipoRequerimento === 'PREENCHIMENTO_ATPVE' && (
        <FormPreenchimentoAtpve cliente={cliente} placaInicial={placaInicial} chassiInicial={chassiInicial} onVeiculoChange={onVeiculoChange} adquirenteInicial={adquirenteInicial} valorInicial={valorInicial} onBloqueioImpressaoChange={onBloqueioImpressaoChange} />
      )}
      {tipoRequerimento === 'DECLARACAO_RESIDENCIA' && (
        <FormDeclaracaoResidencia cliente={cliente} cidadeEmpresa={cidadeEmpresa} />
      )}
    </>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// STYLED COMPONENTS
// ═════════════════════════════════════════════════════════════════════════════

const FormContainer = styled.div`
  width: 210mm;
  margin: 0 auto;
  background: #fff;
  padding: 12mm 9mm;
  box-sizing: border-box;
  font-family: Arial, Helvetica, sans-serif;
  font-size: 9.5pt;
  color: #000;
  border: 1px solid #ccc;
  box-shadow: 0 4px 20px rgba(0,0,0,0.15);

  @media print {
    border: none; box-shadow: none; margin: 0; padding: 0; width: 100%;
  }
`;

const Titulo = styled.div`
  font-size: 9.5pt;
  font-weight: 700;
  text-align: center;
  text-transform: uppercase;
  margin-bottom: 8px;
  letter-spacing: 0.2px;
`;

const BlocoIdentificacao = styled.div`
  margin-bottom: 5px;
  font-size: 9.5pt;
  line-height: 1.7;
`;

const BlocoSeparado = styled.div`
  margin: 12px 0 8px 0;
  font-size: 9.5pt;
`;

const LinhaRestricaoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 4px 8px;
  margin: 6px 0;
  font-size: 9.5pt;
`;

/* ── CORREÇÃO: linha "Eu, NOME , Identidade nº ___" sem quebra ── */
/* Usa display: flex com overflow controlado para não quebrar */
const LinhaInline = styled.div`
  display: flex;
  align-items: baseline;
  flex-wrap: nowrap;
  font-size: 9.5pt;
  line-height: 1.7;
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;

  span {
    font-size: 9.5pt;
    white-space: nowrap;
    flex-shrink: 0;
  }
`;

/* Linha pontilhada que preenche o espaço entre o nome e "Identidade nº" */
const SpanLinha = styled.span`
  flex: 1 1 auto;
  border-bottom: 1px solid #000;
  min-width: 20px;
  display: inline-block;
  margin: 0 2px 2px 2px;
  align-self: flex-end;
`;

const NomeDestaque = styled.span`
  font-weight: 700;
  font-size: 9.5pt;
  text-transform: uppercase;
  white-space: nowrap;
  flex-shrink: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/* LinhaNome mantida para os outros formulários */
const LinhaNome = styled.div`
  display: flex;
  align-items: baseline;
  white-space: nowrap;
  font-size: 9.5pt;
  margin-bottom: 2px;
`;

const NomeSpan = styled.span`
  font-weight: 700;
  font-size: 9.5pt;
  border-bottom: 1px solid #000;
  padding: 0 2px 1px;
  text-transform: uppercase;
  flex: 1;
`;

const LinhaTexto = styled.div`
  font-size: 9.5pt;
  line-height: 1.7;
  display: block;
  span { font-size: 9.5pt; }
`;

const ValorInline = styled.span`
  border-bottom: 1px solid #000;
  padding: 0 2px 1px;
  font-weight: 700;
  font-size: 9.5pt;
`;

const EmailValor = styled.span`
  border-bottom: 1px solid #000;
  padding: 0 2px 1px;
  font-size: 7.5pt;
  font-weight: 700;
  flex: 1;
  text-transform: none;
  letter-spacing: 0;
`;

const LinhaCampo = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 3px 5px;
  margin-bottom: 5px;
  font-size: 9.5pt;
  line-height: 1.8;
`;

const Rotulo = styled.span`
  white-space: nowrap;
  font-size: 9.5pt;
`;

const CampoValor = styled.span`
  border-bottom: 1px solid #000;
  padding: 0 2px 1px;
  font-weight: 700;
  font-size: 9.5pt;
  display: inline-block;
  text-transform: uppercase;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const InputLinha = styled.input`
  border: none;
  border-bottom: 1px solid #000;
  outline: none;
  font-family: Arial, Helvetica, sans-serif;
  font-size: 9.5pt;
  font-weight: 700;
  color: #000;
  background: transparent;
  padding: 0 2px 1px;
  text-transform: uppercase;
  flex-shrink: 0;

  @media print {
    color: #000;
    &::placeholder { color: transparent; }
  }
`;

const InputLinhaCrescente = styled.input`
  border: none;
  border-bottom: 1px solid #000;
  outline: none;
  font-family: Arial, Helvetica, sans-serif;
  font-size: 9.5pt;
  background: transparent;
  padding: 0 2px 1px;
  flex: 1;
  min-width: 180px;

  @media print {
    &::placeholder { color: transparent; }
  }
`;

/* ---- Serviços ---- */

const ServicosContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0 8px;
  border: 1px solid #000;
  margin: 7px 0;
  padding: 5px 7px;
`;

const ServicosColuna = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2.5px;
`;

const ServicoLinha = styled.div`
  display: flex;
  flex-direction: column;
  font-size: 8.5pt;
  line-height: 1.45;
`;

const CheckWrap = styled.label`
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  font-size: 8.5pt;

  input[type="checkbox"] {
    width: 10px; height: 10px; margin: 0;
    accent-color: #000; flex-shrink: 0;
  }
`;

const SubOpcoes = styled.div`
  display: flex;
  gap: 8px;
  margin-left: 16px;
  font-size: 8.2pt;
`;

const SubOpcaoLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 3px;
  cursor: pointer;
  font-size: 8.2pt;

  input[type="radio"] { width: 9px; height: 9px; margin: 0; }
`;

const InputOutro = styled.input`
  border: none;
  border-bottom: 1px solid #000;
  outline: none;
  font-size: 8.5pt;
  font-family: Arial, Helvetica, sans-serif;
  background: transparent;
  margin-left: 4px;
  width: 90%;
  margin-top: 2px;

  @media print {
    &::placeholder { color: transparent; }
  }
`;

/* ---- Restrição / Linhas ---- */

const LinhaRestricao = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 10px;
  margin: 4px 0;
  font-size: 9.5pt;
`;

const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 9.5pt;
  cursor: pointer;
  white-space: nowrap;

  input[type="radio"] { width: 10px; height: 10px; margin: 0; }
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 9.5pt;
  cursor: pointer;
  white-space: nowrap;

  input[type="checkbox"] { width: 10px; height: 10px; margin: 0; }
`;

/* ---- Seções ---- */

const SecaoBox = styled.div`
  border: 1px solid #000;
  padding: 6px 8px;
  margin: 8px 0;
`;

const SecaoTitulo = styled.div`
  font-size: 9pt;
  font-weight: 700;
  text-transform: uppercase;
  margin-bottom: 2px;
`;

const SecaoSubtitulo = styled.div`
  font-size: 8.5pt;
  margin-bottom: 5px;
  font-style: italic;
`;

/* ---- Declaração ---- */

const DeclaracaoBox = styled.div`
  border: 1px solid #000;
  padding: 8px 10px;
  margin: 8px 0;
  page-break-inside: avoid;
`;

const DeclaracaoTexto = styled.p`
  font-size: 9pt;
  line-height: 1.55;
  margin: 0;
  text-align: justify;
`;

/* ---- Assinatura ---- */

const AssinaturaContainer = styled.div`
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  width: 100%;
  page-break-inside: avoid;
`;

const AssinaturaContainerCentralizada = styled.div`
  margin-top: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  width: 100%;
  page-break-inside: avoid;
`;

const MunicipioLinha = styled.div`
  font-size: 9.5pt;
  text-align: center;
  margin-bottom: 4px;
`;

const EspacoAssinatura = styled.div`
  height: 50px;
  width: 65%;
  border-bottom: 1px solid #000;
  box-sizing: border-box;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;

  @media print {
    border-bottom: 1px solid #000 !important;
    display: block !important;
    visibility: visible !important;
  }
`;

const AssinaturaLinha = styled.div`display: none;`;

const AssinaturaRotulo = styled.div`
  font-size: 9.5pt;
  text-align: center;
  max-width: 80%;
`;

export default RequerimentoDetranForm;
